<script>
	/**
	 * [레시피] 탭 — native 타일이 여는 화면의 저작.
	 * 설계: project-le868/launcher-page-design.md §5
	 *
	 * ⭐ 화면 구조: **레시피 고르기 → 단계 탭**(사용자 결정, 2026-10-29).
	 *    예전엔 레시피마다 펼쳐지는 긴 한 장이었다 — 안내·만드는 중·결과 전·결과·버튼이 한꺼번에 쏟아져
	 *    지금 무엇을 만지는지, 그게 화면 어디인지가 안 보였다. 이제 단계 하나씩 보고, **고른 단계가
	 *    오른쪽 미리보기에 뜬다**(dispatch focus → LauncherBuilder → 런타임 recipeStep).
	 * ⭐ 레시피 종류 두 가지:
	 *    generate  안내 → 만드는 중 → (결과 전) → 결과 → 버튼 (운세·행운강화·성향테스트 — 도메인 어휘 없음 §0-1)
	 *    event     경품 이벤트 — EventRecipeEditor 가 자기 단계(기본·경품·방식·팝업·현황)를 그린다
	 * ⚠️ 결과 본문(fallbackBody)과 가중치(weight)는 **런타임으로 내려가지 않는다.** 추첨과 본문
	 *    생성은 서버가 한다 — 클라이언트에 주면 결과를 미리 다 볼 수 있어 "뽑기"가 의미를 잃는다.
	 */
	import { createEventDispatcher, onMount } from "svelte";
	import { Section, Input, Textarea, Select, Button, Switch, Tabs, Badge } from "../../lib/ui";
	import ImagePicker from "../chat-builder/ImagePicker.svelte";
	import EventRecipeEditor from "./EventRecipeEditor.svelte";
	import ImageSlot from "./ImageSlot.svelte";
	import FieldRow from "./FieldRow.svelte";
	import Callout from "./Callout.svelte";
	import { botAssetFolder } from "../chat-builder/imageAssetApi.js";
	import {
		RESULT_SOURCES,
		ACTION_KINDS,
		REVEAL_ENTRANCES,
		REVEAL_GLOWS,
		REVEAL_DIMS,
		DEFAULT_REVEAL_DIM,
		REVEAL_BUBBLES,
		REVEAL_BUBBLE_DEFAULT,
		REVEAL_MOTIONS,
		revealMotionOf,
		contrastRatio,
		newRecipe,
		newEventRecipe,
		EVENT_MODES,
		newCard,
		tokenListOf,
	} from "./launcherSlots.js";

	export let bot;
	/** 서버 RECIPES 칸(배열). */
	export let recipes = [];
	/** assetId → URL. 썸네일용(저장하지 않는다). */
	export let recipeUrls = {};
	/** [타일] 탭이 만든 타일들 — 후속 버튼의 '타일 열기' 대상 고르기에 쓴다. */
	export let tiles = [];
	/**
	 * [신원] 탭이 정의한 받을 정보 칸들 — 머리말에 쓸 수 있는 `{profile.키}` 목록을 **선언에서**
	 * 그려 보여주려고 읽는다(저장하지 않는다). 손으로 적으면 칸 이름을 바꿀 때 안내만 옛것으로 남는다.
	 */
	export let profileFields = [];
	/**
	 * 테마 색 둘 — 결과 전 말풍선 글자가 바탕 위에서 읽히는지 보려고 받는다(저장하지 않는다).
	 * ⚠️ 비면 런타임 기본값과 같은 값으로 본다(config.ts DEFAULT_THEME).
	 */
	export let themeText = "";
	export let themeSurface = "";

	const dispatch = createEventDispatcher();
	$: list = Array.isArray(recipes) ? recipes : [];
	const emit = (next) => dispatch("change", next);

	const patch = (id, fields) => emit(list.map((r) => (r.id === id ? { ...r, ...fields } : r)));
	const patchIn = (r, section, fields) => patch(r.id, { [section]: { ...(r[section] || {}), ...fields } });

	/* ── 고른 레시피 · 단계 ─────────────────────────────────────────────── */
	/** 운세형 레시피의 단계. `preview` = 런타임 RecipeScreen 이 띄울 단계(버튼은 결과 화면에 있다). */
	const GEN_STEPS = [
		{ key: "intro", label: "① 안내", preview: "intro" },
		{ key: "loading", label: "② 만드는 중", preview: "loading" },
		{ key: "reveal", label: "③ 결과 전", preview: "reveal" },
		{ key: "result", label: "④ 결과", preview: "result" },
		{ key: "actions", label: "⑤ 결과 버튼", preview: "result" },
	];
	/** 경품 이벤트의 단계(EventRecipeEditor 가 그린다). */
	const EVENT_STEPS = [
		{ key: "basic", label: "① 기본" },
		{ key: "prizes", label: "② 경품" },
		{ key: "mode", label: "③ 당첨 방식" },
		{ key: "popups", label: "④ 팝업" },
		{ key: "report", label: "⑤ 당첨 현황" },
	];

	let selectedId = "";
	let step = "intro";
	// 고른 게 지워졌거나 아직 없으면 첫 레시피로.
	$: if (list.length && !list.some((r) => r.id === selectedId)) selectRecipe(list[0].id);
	$: cur = list.find((r) => r.id === selectedId) || null;
	$: isEvent = cur?.kind === "event";
	$: steps = isEvent ? EVENT_STEPS : GEN_STEPS;

	/**
	 * 고른 단계를 **미리보기에 띄운다**.
	 * ⚠️ 이벤트의 팝업 단계는 EventRecipeEditor 가 자기 하위 탭(당첨·꽝·안내)으로 다시 가리킨다.
	 * @param replay "now" = 처음부터 다시 / "next" = 저장 뒤 새 설정과 함께 다시(결과 전 연출 값 바꿨을 때)
	 */
	const focusStep = (r = cur, s = step, replay = "") => {
		if (!r) return;
		if (r.kind === "event") {
			dispatch("focus", { kind: "event", recipeId: r.id, popup: s === "popups" ? "win" : "", replay });
		} else {
			const pv = GEN_STEPS.find((x) => x.key === s)?.preview || "intro";
			dispatch("focus", { kind: "step", recipeId: r.id, step: pv, replay });
		}
	};
	const selectRecipe = (id) => {
		selectedId = id;
		const r = list.find((x) => x.id === id);
		step = r?.kind === "event" ? "basic" : "intro";
		focusStep(r, step);
	};
	const setStep = (s) => {
		step = s;
		focusStep(cur, s, "now");
	};
	// 탭에 들어오면 지금 고른 레시피·단계를 미리보기에 띄운다(다른 탭에서 넘어오면 프리뷰가 홈으로 돌아가 있다).
	onMount(() => {
		if (cur) focusStep(cur, step);
	});

	const kindLabel = (r) => (r?.kind === "event" ? "경품 이벤트" : "만들어 주기");
	const summaryOf = (r) => {
		if (!r) return "";
		if (r.kind === "event") {
			const mode = EVENT_MODES.find((m) => m.value === (r.event?.mode || "draw"))?.label || "";
			const period = r.event?.startDate || r.event?.endDate ? ` · ${r.event?.startDate || "…"} ~ ${r.event?.endDate || "…"}` : "";
			return `${mode} · 경품 ${(r.event?.prizes || []).length}개${period}`;
		}
		const src = RESULT_SOURCES.find((s) => s.value === (r.result?.source || "pool"))?.label || "";
		return `${src} · 카드 ${(r.result?.pool || []).length}장${r.reveal?.enabled ? " · 결과 전 화면 켬" : ""}`;
	};

	/**
	 * 결과 전 연출 값 하나를 바꾸고 프리뷰를 **그 값으로** 다시 돌린다.
	 * ⚠️ 바로 다시 돌리면 바뀌기 전 설정으로 한 번 돌고 끝난다(저장이 비동기) → "next".
	 */
	const patchReveal = (r, fields) => {
		patchIn(r, "reveal", fields);
		dispatch("focus", { kind: "step", recipeId: r.id, step: "reveal", replay: "next" });
	};

	/** ⭐ 경품 이벤트 한 벌. 운세 레시피와 같은 목록에 산다(타일·결과 버튼이 같은 방식으로 가리킨다). */
	const addEvent = () => {
		const r = newEventRecipe();
		emit([...list, r]);
		selectRecipe(r.id);
	};
	const add = () => {
		const r = newRecipe();
		emit([...list, r]);
		selectRecipe(r.id);
	};
	const remove = (id) => {
		if (!confirm("이 레시피를 지울까요? 되돌릴 수 없습니다.")) return;
		emit(list.filter((r) => r.id !== id));
	};

	/* ── 결과 카드 ──────────────────────────────────────────────────────── */
	const patchCard = (r, cardId, fields) =>
		patchIn(r, "result", {
			pool: (r.result?.pool || []).map((c) => (c.id === cardId ? { ...c, ...fields } : c)),
		});
	const addCard = (r) => patchIn(r, "result", { pool: [...(r.result?.pool || []), newCard()] });
	const removeCard = (r, cardId) =>
		patchIn(r, "result", { pool: (r.result?.pool || []).filter((c) => c.id !== cardId) });

	/* ── 카드 한꺼번에 ──────────────────────────────────────────────────── */
	/**
	 * ⭐ 그림 여러 장 → 카드 여러 장 / 짝 그림 여러 장 → **이름이 같은** 카드에.
	 *    카드 수십 장(예: 행운빵 80종 × 기본·강화)을 한 장씩 만들고 고르지 않게 하려는 것.
	 *
	 * ⚠️ 이름의 기준은 **자산 이름 = 올린 파일 이름**이다. 맥에서 올린 한글 파일 이름은 자모가
	 *    분해된 형태(NFD)로 올 수 있어 NFC 로 맞추고, 확장자·겹친 공백을 지운다. 안 맞추면
	 *    눈으로는 같은 이름인데 짝이 하나도 안 붙는다.
	 * ⚠️ 짝 그림은 **이름으로만** 붙인다(순서로 붙이지 않는다) — 올린 순서·목록 정렬은 언제든 바뀐다.
	 */
	const cardNameOf = (fileName) =>
		String(fileName || "")
			.normalize("NFC")
			.trim()
			.replace(/\.(png|jpe?g|webp|gif|svg|avif)$/i, "")
			.replace(/\s+/g, " ")
			.trim();
	const nameKey = (s) => cardNameOf(s).toLowerCase();
	/** 긴 이름 목록은 앞 몇 개만. */
	const listNames = (names, max = 8) =>
		names.length > max ? `${names.slice(0, max).join(", ")} 외 ${names.length - max}개` : names.join(", ");

	let bulkOpen = false;
	/** { recipeId, key } — key 가 없으면 카드 만들기, 있으면 그 이름의 짝 그림 붙이기. */
	let bulk = null;
	/** 마지막 결과 한 줄 { recipeId, variant, text }. */
	let bulkNote = null;
	const openBulk = (r, key = "") => {
		bulk = { recipeId: r.id, key };
		bulkNote = null;
		bulkOpen = true;
	};
	/** 카드 만들기면 이미 카드에 쓴 그림은 '추가됨'으로 막는다(같은 그림으로 카드가 두 장 생기지 않게). */
	$: bulkExisting =
		bulk && !bulk.key
			? (list.find((x) => x.id === bulk.recipeId)?.result?.pool || []).map((c) => c.imageAssetId).filter(Boolean)
			: [];

	const onBulkAdd = (e) => {
		const items = e.detail?.items || [];
		const r = list.find((x) => x.id === bulk?.recipeId);
		const key = bulk?.key || "";
		bulk = null;
		if (!r || !items.length) return;
		recipeUrls = { ...recipeUrls, ...Object.fromEntries(items.map((it) => [it.assetId, it.url])) };
		// ⚠️ 고른 것 안에서 이름이 겹치면 **그 이름은 쓰지 않는다** — 기본·짝 폴더를 같이 고르면
		//    같은 이름이 두 장 잡히는데, 어느 쪽이 붙을지는 목록 순서에 달려 엉뚱한 그림이 조용히 붙는다.
		const counts = new Map();
		for (const it of items) counts.set(nameKey(it.name), (counts.get(nameKey(it.name)) || 0) + 1);
		const dupNames = [...new Set(items.filter((it) => counts.get(nameKey(it.name)) > 1).map((it) => cardNameOf(it.name)))];
		const dupLine = dupNames.length
			? `\n같은 이름이 두 장 이상 골라져 뺀 것 ${dupNames.length}개(폴더를 하나만 골라 다시 해주세요): ${listNames(dupNames)}`
			: "";
		const sorted = items
			.filter((it) => counts.get(nameKey(it.name)) === 1)
			.sort((a, b) => cardNameOf(a.name).localeCompare(cardNameOf(b.name), "ko"));
		const pool = r.result?.pool || [];

		if (!key) {
			// 카드 만들기 — 이미 있는 이름은 건너뛴다(다시 돌려도 카드가 두 벌 생기지 않게).
			const taken = new Set(pool.map((c) => nameKey(c.name)).filter(Boolean));
			const added = [];
			const skipped = [];
			for (const it of sorted) {
				const name = cardNameOf(it.name);
				if (!name || taken.has(name.toLowerCase())) {
					skipped.push(name || "(이름 없음)");
					continue;
				}
				taken.add(name.toLowerCase());
				added.push({ ...newCard(), name, imageAssetId: it.assetId });
			}
			if (added.length && !confirm(`카드 ${added.length}장을 만듭니다. 이름은 파일 이름 그대로입니다.`)) return;
			if (added.length) patchIn(r, "result", { pool: [...pool, ...added] });
			bulkNote = {
				recipeId: r.id,
				variant: skipped.length || dupNames.length ? "warn" : "info",
				text:
					`카드 ${added.length}장을 만들었습니다.` +
					(skipped.length ? `\n이미 있는 이름이라 건너뜀 ${skipped.length}개: ${listNames(skipped)}` : "") +
					dupLine,
			};
			return;
		}

		// 짝 그림 붙이기 — 파일 이름 = 카드 이름인 것끼리.
		const byName = new Map(sorted.map((it) => [nameKey(it.name), it]));
		const used = new Set();
		let replaced = 0;
		const next = pool.map((c) => {
			const it = byName.get(nameKey(c.name));
			if (!it) return c;
			used.add(nameKey(c.name));
			if (c.images?.[key] && c.images[key] !== it.assetId) replaced++;
			return { ...c, images: { ...(c.images || {}), [key]: it.assetId } };
		});
		const orphanFiles = sorted.filter((it) => !used.has(nameKey(it.name))).map((it) => cardNameOf(it.name));
		const emptyCards = next.filter((c) => !c.images?.[key]).map((c) => c.name || "(이름 없음)");
		if (used.size) patchIn(r, "result", { pool: next });
		const lines = [`'${key}' 그림을 카드 ${used.size}장에 붙였습니다${replaced ? `(그중 ${replaced}장은 바꿈)` : ""}.`];
		if (orphanFiles.length) lines.push(`이름이 같은 카드가 없는 그림 ${orphanFiles.length}장: ${listNames(orphanFiles)}`);
		if (emptyCards.length) lines.push(`아직 '${key}' 그림이 없는 카드 ${emptyCards.length}장: ${listNames(emptyCards)}`);
		if (dupLine) lines.push(dupLine.trim());
		bulkNote = {
			recipeId: r.id,
			variant: orphanFiles.length || emptyCards.length || dupNames.length ? "warn" : "info",
			text: lines.join("\n"),
		};
	};

	/* ── 후속 버튼 ──────────────────────────────────────────────────────── */
	/** ⑤ '오늘은 이미 받았어요' 창 미리보기 — all = 이벤트도 다 했을 때 모양(LauncherBuilder `done:<id>[:all]`). */
	const focusDone = (r, all) => dispatch("focus", { kind: "done", recipeId: r.id, all });
	/** 결과 버튼 중 경품 이벤트로 가는 게 있나 — 있어야 그 창이 뜬다(없으면 문구 한 줄). */
	const hasEventAction = (r) =>
		(r?.actions || []).some((a) => a?.kind === "recipe" && list.find((x) => x.id === a.target)?.kind === "event");
	const patchAction = (r, i, fields) =>
		patch(r.id, { actions: (r.actions || []).map((a, n) => (n === i ? { ...a, ...fields } : a)) });
	/**
	 * 이 레시피의 카드에 **짝 그림 칸이 몇 개 필요한지**. 다른 레시피가 이 레시피에서
	 * 이어받으며 `images.<키>` 를 쓰겠다고 선언하면, 그 키의 칸이 여기 자동으로 생긴다.
	 *
	 * ⭐ 저작자가 키를 **두 번 적을 일이 없다** — 쓰는 쪽(B)에서 한 번 짓고, 올리는 쪽(A)은
	 *    그걸 읽기만 한다. 오타로 짝이 어긋날 여지가 구조적으로 없다.
	 */
	const carriedKeys = (r, all) => {
		const keys = [];
		for (const b of all) {
			const img = b?.result?.image;
			if (!img || img.from !== "prev" || !img.key) continue;
			const linked = (r.actions || []).some(
				(a) => a && a.kind === "recipe" && a.target === b.id && a.carry === true,
			);
			if (linked && !keys.includes(img.key)) keys.push(img.key);
		}
		return keys;
	};

	/** 카드의 짝 그림 하나를 갈아끼운다. */
	const patchCardImage = (r, cardId, key, assetId) => {
		const c = (r.result?.pool || []).find((x) => x.id === cardId);
		patchCard(r, cardId, { images: { ...(c?.images || {}), [key]: assetId } });
	};

	const addAction = (r) =>
		patch(r.id, { actions: [...(r.actions || []), { label: "", kind: "home", target: "" }] });
	const removeAction = (r, i) =>
		patch(r.id, { actions: (r.actions || []).filter((_, n) => n !== i) });

	/* ── 그림 ───────────────────────────────────────────────────────────── */
	let pickerOpen = false;
	let picking = null; // { recipeId, kind:'intro'|'loading'|'reveal'|'result'|'card'|'cardImage', cardId?, imageKey?, slot }

	/** @param slot 'image'(가운데 캐릭터) | 'background'(화면 전체 바탕) | 'stand' | 'title' | 'light' */
	const openPicker = (recipeId, kind, cardId = null, imageKey = null, slot = "image") => {
		picking = { recipeId, kind, cardId, imageKey, slot };
		pickerOpen = true;
	};
	/**
	 * 고른 그림을 **비운다**(잘못 넣었을 때 되돌리는 길).
	 *
	 * ⚠️⚠️ `onPick` 의 분기와 **짝이어야 한다** — 넣는 길만 있고 지우는 길이 없으면 저작자가
	 *    잘못 넣은 그림을 영영 못 치운다(그림 칸은 '비우면 셸 것을 쓴다'가 기본 동작이라
	 *    "안 넣은 상태"가 **의미 있는 선택**이다).
	 * ⚠️ `null` 을 넣는다 — 빈 문자열도 서버 `push()` 가 걸러 주지만(typeof+truthy),
	 *    ScreenContext 가 이미 `null` 로 통일해 뒀다.
	 * ⚠️ 짝 그림(`images[키]`)만은 **키를 지운다** — null 로 남기면 쓰지 않는 키가 카드마다
	 *    쌓이고, 나중에 그 키를 다시 쓸 때 "있는데 비었다"와 구별이 안 된다.
	 */
	const clearImage = (r, kind, cardId = null, imageKey = null, slot = "image") => {
		if (kind === "card") return patchCard(r, cardId, { imageAssetId: null });
		if (kind === "cardImage") {
			const c = (r.result?.pool || []).find((x) => x.id === cardId);
			const images = { ...(c?.images || {}) };
			delete images[imageKey];
			return patchCard(r, cardId, { images });
		}
		if (kind === "reveal") return patchReveal(r, { [slot === "light" ? "lightAssetId" : "backgroundAssetId"]: null });
		if (slot === "background") return patchIn(r, kind, { backgroundAssetId: null });
		if (slot === "stand") return patchIn(r, kind, { standAssetId: null });
		if (slot === "title") return patchIn(r, kind, { titleAssetId: null });
		return patchIn(r, kind, { imageAssetId: null });
	};

	const onPick = (e) => {
		if (!picking) return;
		const { assetId, url } = e.detail || {};
		recipeUrls = { ...recipeUrls, [assetId]: url };
		const r = list.find((x) => x.id === picking.recipeId);
		if (r) {
			if (picking.kind === "card") patchCard(r, picking.cardId, { imageAssetId: assetId });
			else if (picking.kind === "cardImage") patchCardImage(r, picking.cardId, picking.imageKey, assetId);
			// ⭐ 결과 전 화면 그림(배경·빛) — 바꾸면 연출을 새 그림으로 다시 돌린다.
			else if (picking.kind === "reveal")
				patchReveal(r, { [picking.slot === "light" ? "lightAssetId" : "backgroundAssetId"]: assetId });
			// ⚠️ 배경과 그림은 **역할이 다르다** — 배경은 화면을 꽉 채우고, 그림은 그 위 캐릭터다.
			//    한 칸으로 두면 저작자가 올린 배경이 55% 짜리 가운데 일러스트로 그려진다(실제로 그랬다).
			else if (picking.slot === "background") patchIn(r, picking.kind, { backgroundAssetId: assetId });
			// ⭐ 받침 — 카드 그림 **아래에 깔리는 판**(시안의 접시). 배경과 다른 칸이다.
			else if (picking.slot === "stand") patchIn(r, picking.kind, { standAssetId: assetId });
			// ⭐ 타이틀 — 결과 화면 맨 위 그림(리본 배너 등). 글자가 아니라 그림이다.
			else if (picking.slot === "title") patchIn(r, picking.kind, { titleAssetId: assetId });
			else patchIn(r, picking.kind, { imageAssetId: assetId });
		}
		picking = null;
	};

	/**
	 * 이 레시피를 **물려주는 레시피들**(= 내 앞에 오는 레시피).
	 * ⭐ 짝이 성립하려면 **양쪽이 다 있어야 한다**: 앞 레시피 A 의 결과 버튼에
	 *    `{kind:'recipe', target: 나, carry: true}` + 내 쪽 `result.image.from = 'prev'`.
	 *    한쪽만 해두면 런타임에서 **카드 이름도 그림도 없이** 결과가 뜬다(서버가 prev 를 못 찾는다).
	 */
	const carriedFrom = (r, all) =>
		all.filter((a) =>
			(a?.actions || []).some((x) => x && x.kind === "recipe" && x.target === r.id && x.carry === true),
		);

	const usesPool = (r) => (r.result?.source || "pool") !== "llm";
	/**
	 * 결과 전 화면 가운데에 **나올 그림이 있나**. 이어받기면 앞 카드 그림이 오고,
	 * '전부 AI가 쓰기'(카드 없음)면 아무것도 안 나온다 — 빛만 도는 화면이 된다.
	 */
	const revealHasCard = (r) => r.result?.image?.from === "prev" || usesPool(r);

	/** 말풍선 바탕 고르기의 현재 칸('' / 'theme' / 'custom'). 저장값이 '#hex' 면 직접 고르기다. */
	const bubbleMode = (r) => {
		const v = r.reveal?.bubbleColor || "";
		return v === "" || v === "theme" ? v : "custom";
	};
	/** 실제로 칠해질 바탕색(대비 계산용). */
	const bubbleHex = (r) => {
		const v = r.reveal?.bubbleColor || "";
		if (v === "theme") return themeSurface || "#fdf6ec";
		return v || REVEAL_BUBBLE_DEFAULT;
	};
	/** 테마 글자색이 그 바탕 위에서 읽히나. 4.5 미만이면 경고. */
	const bubbleLowContrast = (r) => {
		const ratio = contrastRatio(bubbleHex(r), themeText || "#4a3728");
		return ratio !== null && ratio < 4.5;
	};
	const usesLlm = (r) => (r.result?.source || "pool") !== "pool";
	$: sourceHint = (v) => RESULT_SOURCES.find((s) => s.value === v)?.hint || "";
	$: tokens = tokenListOf(profileFields);
</script>

<div class="tw-flex tw-flex-col tw-gap-6">
	<Section
		title="레시피"
		description="타일이나 다른 레시피의 버튼이 여는 화면입니다. 레시피를 고르고 단계별로 만드세요 — 고른 단계가 오른쪽 미리보기에 뜹니다."
	>
		<!-- ── 고르기 줄 ── -->
		<div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
			{#if list.length > 0}
				<Select class="tw-min-w-0 tw-flex-1" value={selectedId} on:change={(e) => selectRecipe(e.target.value)}>
					{#each list as r (r.id)}
						<option value={r.id}>{r.name || "(이름 없음)"} — {kindLabel(r)}</option>
					{/each}
				</Select>
			{/if}
			<Button variant="outline" size="sm" on:click={add}>+ 레시피</Button>
			<Button variant="outline" size="sm" on:click={addEvent}>+ 경품 이벤트</Button>
		</div>

		{#if list.length === 0}
			<div class="tw-mt-4 tw-rounded-lg tw-border tw-border-dashed tw-border-border tw-py-10 tw-text-center tw-text-sm tw-text-muted-foreground">
				아직 레시피가 없습니다.<br />
				<strong>+ 레시피</strong>(운세처럼 만들어 주는 화면) 또는 <strong>+ 경품 이벤트</strong>(뽑기 등)로 시작하세요.
			</div>
		{/if}

		{#if cur}
			{@const r = cur}
			<div class="tw-mt-5 tw-flex tw-flex-col tw-gap-4">
				<FieldRow label="이름">
					<Input placeholder={isEvent ? "예) 두근두근 행운뽑기" : "예) 오늘의 행운빵 받기"} value={r.name || ""} on:input={(e) => patch(r.id, { name: e.target.value })} />
					<svelte:fragment slot="hint">
						<Badge variant="optional">{kindLabel(r)}</Badge>
						<span class="tw-ml-1">{summaryOf(r)}</span>
						<br />빌더에서 구분하려고 붙이는 이름입니다 — 사용자 화면에는 나오지 않습니다.
					</svelte:fragment>
				</FieldRow>

				<!-- ── 단계 탭 ── -->
				<div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-border-t tw-border-border tw-pt-4">
					<Tabs items={steps} active={step} on:change={(e) => setStep(e.detail)} />
					<button type="button" class="pv-btn tw-ml-auto" title="미리보기를 이 단계 처음부터 다시" on:click={() => focusStep(r, step, "now")}>▶ 미리보기 다시</button>
				</div>

				{#if isEvent}
					<EventRecipeEditor
						{bot}
						recipe={r}
						{step}
						bind:recipeUrls
						{profileFields}
						{tiles}
						recipes={list}
						on:change={(e) => emit(list.map((x) => (x.id === r.id ? e.detail : x)))}
						on:focus={(e) => dispatch("focus", { ...e.detail, kind: "event" })}
					/>
				{:else if step === "intro"}
					<!-- ── ① 안내 ── -->
					<p class="step-desc">레시피를 열면 처음 보이는 화면입니다. <strong>말풍선과 그림을 다 비우면 이 화면은 건너뛰고</strong> 바로 만드는 중으로 갑니다.</p>
					<div class="slots">
						<ImageSlot label="배경" hint="390×844 · 비우면 런처 배경" shape="tall" empty="런처 배경" url={recipeUrls[r.intro?.backgroundAssetId] || ""} filled={!!r.intro?.backgroundAssetId} on:pick={() => openPicker(r.id, "intro", null, null, "background")} on:clear={() => clearImage(r, "intro", null, null, "background")} />
						<ImageSlot label="캐릭터 그림" hint="배경 위에 얹힘" url={recipeUrls[r.intro?.imageAssetId] || ""} filled={!!r.intro?.imageAssetId} on:pick={() => openPicker(r.id, "intro")} on:clear={() => clearImage(r, "intro")} />
					</div>
					<FieldRow label="말풍선" top>
						<Textarea rows="2" placeholder="예) 오늘의 운세를 받아볼래?" value={r.intro?.text || ""} on:input={(e) => patchIn(r, "intro", { text: e.target.value })} />
					</FieldRow>
					<FieldRow label="버튼 문구">
						<Input placeholder="예) 받기 — 비우면 '받기'" value={r.intro?.confirmLabel || ""} on:input={(e) => patchIn(r, "intro", { confirmLabel: e.target.value })} />
					</FieldRow>
				{:else if step === "loading"}
					<!-- ── ② 만드는 중 ── -->
					<p class="step-desc">결과를 만드는 동안 보이는 화면입니다. 진행바가 차오르고, <strong>최소 시간</strong>이 지나야 다음으로 넘어갑니다.</p>
					<div class="slots">
						<ImageSlot label="배경" hint="390×844 · 비우면 런처 배경" shape="tall" empty="런처 배경" url={recipeUrls[r.loading?.backgroundAssetId] || ""} filled={!!r.loading?.backgroundAssetId} on:pick={() => openPicker(r.id, "loading", null, null, "background")} on:clear={() => clearImage(r, "loading", null, null, "background")} />
						<ImageSlot label="캐릭터 그림" hint="배경 위에 얹힘" url={recipeUrls[r.loading?.imageAssetId] || ""} filled={!!r.loading?.imageAssetId} on:pick={() => openPicker(r.id, "loading")} on:clear={() => clearImage(r, "loading")} />
					</div>
					<FieldRow label="제목">
						<Input placeholder="예) 행운빵을 굽는 중…" value={r.loading?.title || ""} on:input={(e) => patchIn(r, "loading", { title: e.target.value })} />
					</FieldRow>
					<FieldRow label="말풍선" top>
						<Textarea rows="2" placeholder="예) 오늘의 행운빵은 무엇일까? 조금만 기다려줘!" value={r.loading?.desc || ""} on:input={(e) => patchIn(r, "loading", { desc: e.target.value })} />
					</FieldRow>
					<FieldRow label="최소 시간">
						<div class="tw-flex tw-items-center tw-gap-2">
							<Input class="tw-w-28" type="number" min="0" step="100" value={r.loading?.minMs ?? 1800} on:input={(e) => patchIn(r, "loading", { minMs: Number(e.target.value) || 0 })} />
							<span class="tw-text-xs tw-text-muted-foreground">ms</span>
						</div>
						<svelte:fragment slot="hint">너무 짧으면 번쩍 지나가 뽑는 느낌이 안 납니다.</svelte:fragment>
					</FieldRow>
				{:else if step === "reveal"}
					<!-- ── ③ 결과 전 ── -->
					<div class="tw-flex tw-items-center tw-gap-3">
						<Switch checked={r.reveal?.enabled === true} on:click={() => patchReveal(r, { enabled: !(r.reveal?.enabled === true) })} aria-label="결과 전 화면 쓰기" />
						<span class="tw-text-sm tw-font-semibold">결과 전 화면 쓰기</span>
					</div>
					<p class="step-desc">
						만드는 중이 끝나면 <strong>뽑힌 카드 그림</strong>이 빛과 함께 나타나고, 버튼을 눌러야 결과가 펼쳐집니다.
						가운데 그림은 따로 고르지 않습니다 — ④ 결과의 카드에서 뽑힌 그림이 나옵니다.
					</p>
					{#if !r.reveal?.enabled}
						<Callout>꺼져 있습니다 — 지금은 만드는 중에서 바로 결과로 갑니다. 켜면 아래 설정이 열립니다.</Callout>
					{:else}
						{#if !revealHasCard(r)}
							<Callout variant="warn"><strong>'전부 AI가 쓰기'는 카드를 뽑지 않아</strong> 가운데 그림이 없습니다 — 빛과 말풍선만 보입니다.</Callout>
						{/if}
						<div class="slots">
							<ImageSlot label="배경" hint="390×844 · 비우면 런처 배경" shape="tall" empty="런처 배경" url={recipeUrls[r.reveal?.backgroundAssetId] || ""} filled={!!r.reveal?.backgroundAssetId} on:pick={() => openPicker(r.id, "reveal", null, null, "background")} on:clear={() => clearImage(r, "reveal", null, null, "background")} />
							{#if (r.reveal?.glow || "rays") !== "none"}
								<ImageSlot label="빛 그림" hint="정사각형 · 비우면 기본 빛" empty="기본 빛" url={recipeUrls[r.reveal?.lightAssetId] || ""} filled={!!r.reveal?.lightAssetId} on:pick={() => openPicker(r.id, "reveal", null, null, "light")} on:clear={() => clearImage(r, "reveal", null, null, "light")} />
							{/if}
						</div>

						<div class="group-title">말풍선과 버튼</div>
						<FieldRow label="말풍선" top>
							<Textarea rows="2" placeholder={"예) 짜잔! 행운빵이 다 구워졌어!\n운세결과를 확인하러 가볼까?"} value={r.reveal?.text || ""} on:input={(e) => patchIn(r, "reveal", { text: e.target.value })} />
							<svelte:fragment slot="hint">
								{#if !(r.reveal?.text || "").trim()}<span class="tw-text-amber-600">지금은 비어 있어 말풍선 없이 버튼만 나옵니다. </span>{/if}
								쓸 수 있는 값: <code>{"{card.name}"}</code>(뽑힌 카드 이름){#each tokens as tk (tk)} <code>{tk}</code>{/each}
							</svelte:fragment>
						</FieldRow>
						<FieldRow label="버튼 문구">
							<Input placeholder="예) 운세결과 확인하기 — 비우면 '결과 보기'" value={r.reveal?.confirmLabel || ""} on:input={(e) => patchIn(r, "reveal", { confirmLabel: e.target.value })} />
						</FieldRow>
						<FieldRow label="말풍선 색">
							<div class="tw-flex tw-items-center tw-gap-2">
								<Select class="tw-min-w-0 tw-flex-1" value={bubbleMode(r)} on:change={(e) => patchReveal(r, { bubbleColor: e.target.value === "custom" ? bubbleHex(r) : e.target.value })}>
									{#each REVEAL_BUBBLES as o (o.value)}<option value={o.value}>{o.label}</option>{/each}
								</Select>
								{#if bubbleMode(r) === "custom"}
									<!-- ⚠️ on:change 로만 저장한다 — 색 고르는 창을 끄는 동안 input 이 수십 번 온다. -->
									<input type="color" class="color-input" value={bubbleHex(r)} on:change={(e) => patchReveal(r, { bubbleColor: e.target.value })} aria-label="말풍선 색 고르기" />
								{/if}
								<span class="swatch" style="background: {bubbleHex(r)}" title="지금 말풍선 바탕"></span>
							</div>
							<svelte:fragment slot="hint">어둡게 덮은 장면이라 흰색이 기본입니다. 글자는 [디자인] 탭의 글꼴·글자 색을 쓰고, 말풍선 바탕색으로 외곽선이 둘립니다.</svelte:fragment>
						</FieldRow>
						{#if bubbleLowContrast(r)}
							<Callout variant="warn">이 바탕에서는 글자색과 대비가 낮아 <strong>잘 안 읽힙니다</strong> — 더 밝은 색을 고르거나 [디자인] 탭의 글자 색을 바꿔주세요.</Callout>
						{/if}

						<div class="group-title">연출 <span class="tw-font-normal tw-text-muted-foreground">— 바꾸면 미리보기가 처음부터 다시 돕니다</span></div>
						<FieldRow label="나타나기">
							<Select value={r.reveal?.entrance || REVEAL_ENTRANCES[0].value} on:change={(e) => patchReveal(r, { entrance: e.target.value })}>
								{#each REVEAL_ENTRANCES as o (o.value)}<option value={o.value}>{o.label}</option>{/each}
							</Select>
							<svelte:fragment slot="hint">{REVEAL_ENTRANCES.find((o) => o.value === (r.reveal?.entrance || REVEAL_ENTRANCES[0].value))?.hint || ""}</svelte:fragment>
						</FieldRow>
						<FieldRow label="카드 움직임">
							<!-- ⚠️ 옛 스위치 값(bounce)은 고르는 순간 지운다 — 남겨 두면 motion 과 둘이 따로 논다. -->
							<Select value={revealMotionOf(r.reveal)} on:change={(e) => patchReveal(r, { motion: e.target.value, bounce: undefined })}>
								{#each REVEAL_MOTIONS as o (o.value)}<option value={o.value}>{o.label}</option>{/each}
							</Select>
							<svelte:fragment slot="hint">{REVEAL_MOTIONS.find((o) => o.value === revealMotionOf(r.reveal))?.hint || ""} 카드가 다 나타난 뒤부터 움직입니다.</svelte:fragment>
						</FieldRow>
						<FieldRow label="뒤에서 빛">
							<Select value={r.reveal?.glow || REVEAL_GLOWS[0].value} on:change={(e) => patchReveal(r, { glow: e.target.value })}>
								{#each REVEAL_GLOWS as o (o.value)}<option value={o.value}>{o.label}</option>{/each}
							</Select>
							<svelte:fragment slot="hint">{REVEAL_GLOWS.find((o) => o.value === (r.reveal?.glow || REVEAL_GLOWS[0].value))?.hint || ""}</svelte:fragment>
						</FieldRow>
						<FieldRow label="배경 어둡게">
							<Select value={String(r.reveal?.dim ?? DEFAULT_REVEAL_DIM)} on:change={(e) => patchReveal(r, { dim: Number(e.target.value) })}>
								{#each REVEAL_DIMS as o (o.value)}<option value={String(o.value)}>{o.label}</option>{/each}
							</Select>
						</FieldRow>
						<FieldRow label="반짝이 별">
							<div class="tw-flex tw-min-h-10 tw-items-center tw-gap-2 tw-text-sm">
								<Switch checked={r.reveal?.sparkle !== false} on:click={() => patchReveal(r, { sparkle: r.reveal?.sparkle === false })} aria-label="반짝이 별" />
								<span class="tw-text-xs tw-text-muted-foreground">카드 둘레에서 별이 깜빡입니다</span>
							</div>
						</FieldRow>
						<Callout>
							이 화면엔 <strong>뒤로 화살표가 없습니다</strong>(결과는 이미 받았습니다 — 버튼으로만 넘어갑니다).
							휴대폰에서 '동작 줄이기'를 켠 사용자에겐 움직임 없이 멈춘 화면으로 보입니다.
						</Callout>
					{/if}
				{:else if step === "result"}
					<!-- ── ④ 결과 ── -->
					<p class="step-desc">뽑힌 카드(그림·이름)와 본문이 나오는 화면입니다. 본문은 <strong>만드는 방법</strong>에 따라 미리 쓴 글이 나가거나 AI가 씁니다.</p>
					<div class="slots">
						<ImageSlot label="배경" hint="390×844 · 비우면 테마 바탕색" shape="tall" empty="테마 바탕" url={recipeUrls[r.result?.backgroundAssetId] || ""} filled={!!r.result?.backgroundAssetId} on:pick={() => openPicker(r.id, "result", null, null, "background")} on:clear={() => clearImage(r, "result", null, null, "background")} />
						<ImageSlot label="타이틀 그림" hint="화면 맨 위 · 296×65" shape="wide" url={recipeUrls[r.result?.titleAssetId] || ""} filled={!!r.result?.titleAssetId} on:pick={() => openPicker(r.id, "result", null, null, "title")} on:clear={() => clearImage(r, "result", null, null, "title")} />
						<ImageSlot label="받침" hint="카드 아래 · 190×74" shape="wide" url={recipeUrls[r.result?.standAssetId] || ""} filled={!!r.result?.standAssetId} on:pick={() => openPicker(r.id, "result", null, null, "stand")} on:clear={() => clearImage(r, "result", null, null, "stand")} />
					</div>

					<div class="group-title">본문</div>
					<FieldRow label="만드는 방법">
						<Select value={r.result?.source || "pool"} on:change={(e) => patchIn(r, "result", { source: e.target.value })}>
							{#each RESULT_SOURCES as s (s.value)}<option value={s.value}>{s.label}</option>{/each}
						</Select>
						<svelte:fragment slot="hint">{sourceHint(r.result?.source || "pool")}</svelte:fragment>
					</FieldRow>
					<FieldRow label="첫 줄 (머리말)" top>
						<Textarea rows="2" placeholder="예) 망소금이 {'{nickname}'}을(를) 위해 {'{card.name}'}을 구워왔어!" value={r.result?.lead || ""} on:input={(e) => patchIn(r, "result", { lead: e.target.value })} />
						<svelte:fragment slot="hint">
							본문 <strong>맨 앞</strong>에 한 줄 띄우고 그대로 붙습니다(AI에게 맡기지 않아 이름이 틀리지 않습니다).
							조사는 <strong>"을(를)"</strong>처럼 적어주세요. 쓸 수 있는 값: <code>{"{card.name}"}</code>{#each tokens as tk (tk)} <code>{tk}</code>{/each}
						</svelte:fragment>
					</FieldRow>
					<FieldRow label="카드 꼬리표">
						<Input placeholder="예) 행운 강화.ver" value={r.result?.tagline || ""} on:input={(e) => patchIn(r, "result", { tagline: e.target.value })} />
						<svelte:fragment slot="hint">카드 이름칩 위에 걸치는 작은 말 — 이 레시피의 모든 카드에 붙습니다. 비우면 안 나옵니다.</svelte:fragment>
					</FieldRow>

					{#if usesLlm(r)}
						<div class="group-title">AI 지시</div>
						<FieldRow label="지시" top>
							<Textarea rows="4" placeholder={"예) {nickname}에게 오늘 하루를 다정하게 이야기해줘. 뽑힌 것은 {card.name}이야."} value={r.result?.llm?.prompt || ""} on:input={(e) => patchIn(r, "result", { llm: { ...(r.result?.llm || {}), prompt: e.target.value } })} />
							<svelte:fragment slot="hint"><code>{"{card.name}"}</code>{#each tokens as tk (tk)} <code>{tk}</code>{/each} 를 쓸 수 있습니다.</svelte:fragment>
						</FieldRow>
						<FieldRow label="이어받았을 때" top>
							<!-- ⭐ 같은 레시피라도 **진입점**에 따라 입력이 다르다. 프롬프트를 하나로 두고
							     {prev.body} 를 비우면, 메인에서 들어왔을 때 "앞에서 다룬 건 빼고" 가 빈 채로
							     남아 AI 가 없는 맥락을 지어낸다. 그래서 **있을 때만 들어가는 칸**으로 쪼갰다. -->
							<Textarea rows="3" placeholder={"예) 방금 이런 글을 건넸어: \"{prev.body}\"\n거기서 이미 다룬 건 빼고 써줘."} value={r.result?.llm?.prevPrompt || ""} on:input={(e) => patchIn(r, "result", { llm: { ...(r.result?.llm || {}), prevPrompt: e.target.value } })} />
							<svelte:fragment slot="hint">다른 레시피의 결과 버튼에서 <strong>물려주기</strong>로 들어왔을 때만 덧붙습니다. <code>{"{prev.body}"}</code> <code>{"{prev.card.name}"}</code></svelte:fragment>
						</FieldRow>
						<FieldRow label="길이 · 반복">
							<div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-xs tw-text-muted-foreground">
								<Input class="tw-w-24" type="number" min="100" value={r.result?.llm?.maxChars ?? 900} on:input={(e) => patchIn(r, "result", { llm: { ...(r.result?.llm || {}), maxChars: Number(e.target.value) || 900 } })} />
								<span>자 이내 · 최근</span>
								<Input class="tw-w-20" type="number" min="0" max="60" value={r.result?.llm?.avoidRepeatDays ?? 7} on:input={(e) => patchIn(r, "result", { llm: { ...(r.result?.llm || {}), avoidRepeatDays: Math.max(0, Math.min(60, Number(e.target.value) || 0)) } })} />
								<span>일 안에 쓴 글과 겹치지 않게(0=끔)</span>
							</div>
						</FieldRow>
					{/if}

					<div class="group-title">결과 카드 그림</div>
					<FieldRow label="그림 가져오기">
						<div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
							<Select class="tw-w-48" value={r.result?.image?.from || "own"} on:change={(e) => patchIn(r, "result", { image: { ...(r.result?.image || {}), from: e.target.value } })}>
								<option value="own">이 레시피에서 뽑기</option>
								<option value="prev">이어받은 카드 쓰기</option>
							</Select>
							{#if r.result?.image?.from === "prev"}
								<Input class="tw-w-40" placeholder="어느 모습? (예: 소금)" value={r.result?.image?.key || ""} on:input={(e) => patchIn(r, "result", { image: { ...(r.result?.image || {}), key: e.target.value.trim() } })} />
							{/if}
						</div>
						<svelte:fragment slot="hint">
							{#if r.result?.image?.from === "prev"}
								앞 레시피에서 뽑힌 카드를 그대로 씁니다. 이름을 적으면 그 이름의 <strong>짝 그림</strong>을(앞 레시피 카드마다 그 칸이 생깁니다), 비우면 기본 그림을 씁니다.
							{:else}
								아래 카드 중 하나가 가중치대로 뽑힙니다.
							{/if}
						</svelte:fragment>
					</FieldRow>
					{#if r.result?.image?.from === "prev"}
						{#if carriedFrom(r, list).length === 0}
							<Callout variant="error">
								이 레시피로 <strong>물려주는 레시피가 없습니다.</strong> 앞 레시피의 [⑤ 결과 버튼]을 '다른 레시피 열기 → 이 레시피'로 두고
								<strong>결과 물려주기</strong>를 켜주세요. 그러지 않으면 카드 이름도 그림도 없이 결과가 뜹니다.
							</Callout>
						{:else}
							<Callout>{carriedFrom(r, list).map((a) => a.name || "(이름 없는 레시피)").join(", ")}에서 이어받습니다 — 이 레시피에는 카드를 따로 만들 필요가 없습니다.</Callout>
						{/if}
						<!-- ⚠️⚠️ 이어받은 카드에는 **카드별 '미리 쓴 글'이 없다**(이름·그림만 따라온다) →
						     '미리 쓴 글에서 뽑기'로 두면 본문이 조용히 빈 채로 나간다. -->
						{#if (r.result?.source || "pool") === "pool"}
							<Callout variant="error"><strong>'미리 쓴 글에서 뽑기'와는 같이 쓸 수 없습니다</strong> — 이어받는 건 이름과 그림뿐이라 본문이 빈 채로 나갑니다. 만드는 방법을 AI가 쓰는 쪽으로 바꿔주세요.</Callout>
						{/if}
					{/if}

					{#if usesPool(r)}
						<div class="tw-flex tw-flex-col tw-gap-2">
							<!-- ⭐ 한꺼번에 — 카드가 수십 장이면 목록 아래 버튼은 찾기 어려워서 목록 **위**에 둔다. -->
							<div class="bulk-bar">
								<span class="tw-text-xs tw-font-semibold">카드 {(r.result?.pool || []).length}장</span>
								<Button variant="outline" size="sm" on:click={() => openBulk(r)}>그림 여러 장으로 카드 만들기</Button>
								{#each carriedKeys(r, list) as k (k)}
									<Button variant="outline" size="sm" on:click={() => openBulk(r, k)}>'{k}' 그림 한꺼번에 붙이기</Button>
								{/each}
							</div>
							<p class="step-desc">
								이미지 라이브러리에서 폴더를 만들어 그림을 한꺼번에 끌어다 올린 뒤, <strong>보이는 것 모두 선택</strong> → 추가.
								카드 이름은 <strong>파일 이름</strong>(확장자 빼고)이 됩니다.
								{#if carriedKeys(r, list).length}짝 그림은 파일 이름이 카드 이름과 <strong>같은 것끼리</strong> 붙습니다.{/if}
							</p>
							{#if bulkNote && bulkNote.recipeId === r.id}
								<Callout variant={bulkNote.variant}><span class="bulk-note">{bulkNote.text}</span></Callout>
							{/if}
							{#each r.result?.pool || [] as c (c.id)}
								<div class="card-row">
									<ImageSlot label="그림" url={recipeUrls[c.imageAssetId] || ""} filled={!!c.imageAssetId} on:pick={() => openPicker(r.id, "card", c.id)} on:clear={() => clearImage(r, "card", c.id)} />
									<!-- ⭐ 짝 그림. 이어받는 레시피가 그 키를 쓰겠다고 해서 생긴 칸이다. -->
									{#each carriedKeys(r, list) as k (k)}
										<ImageSlot label={`'${k}' 그림`} hint="이어받는 쪽" url={recipeUrls[c.images?.[k]] || ""} filled={!!c.images?.[k]} on:pick={() => openPicker(r.id, "cardImage", c.id, k)} on:clear={() => clearImage(r, "cardImage", c.id, k)} />
									{/each}
									<div class="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-2">
										<div class="tw-flex tw-items-center tw-gap-2">
											<Input class="tw-min-w-0 tw-flex-1" placeholder="카드 이름 (예: 몽글몽글 구름크림빵)" value={c.name || ""} on:input={(e) => patchCard(r, c.id, { name: e.target.value })} />
											<span class="tw-shrink-0 tw-text-xs tw-text-muted-foreground">가중치</span>
											<Input class="tw-w-20" type="number" min="0" title="잘 나올 확률(가중치)" value={c.weight ?? 1} on:input={(e) => patchCard(r, c.id, { weight: Number(e.target.value) })} />
											<button type="button" class="del-btn" aria-label="카드 삭제" on:click={() => removeCard(r, c.id)}>✕</button>
										</div>
										<Textarea rows="2" placeholder={usesLlm(r) ? "AI 가 실패했을 때 대신 나갈 글" : "이 카드가 뽑히면 보여줄 글"} value={c.fallbackBody || ""} on:input={(e) => patchCard(r, c.id, { fallbackBody: e.target.value })} />
									</div>
								</div>
							{/each}
							<div><Button variant="outline" size="sm" on:click={() => addCard(r)}>+ 카드 추가</Button></div>
							{#if usesLlm(r)}
								<Callout variant="warn">대신 나갈 글을 비워 두면, AI 호출이 실패했을 때 <strong>빈 결과</strong>가 나갑니다.</Callout>
							{/if}
						</div>
					{/if}
				{:else if step === "actions"}
					<!-- ── ⑤ 결과 버튼 ── -->
					<p class="step-desc">결과 화면 아래 버튼입니다. 마지막 버튼이 강조 버튼이 됩니다. <strong>다른 레시피 열기</strong>로 이어 붙이면 흐름이 이어집니다(예: 행운 강화하기, 경품 이벤트).</p>
					<div class="tw-flex tw-flex-col tw-gap-2">
						{#each r.actions || [] as a, i}
							<div class="action-row">
								<Input class="tw-min-w-0 tw-flex-1" placeholder="버튼 문구" value={a.label || ""} on:input={(e) => patchAction(r, i, { label: e.target.value })} />
								<Select class="tw-w-44" value={a.kind} on:change={(e) => patchAction(r, i, { kind: e.target.value, target: "" })}>
									{#each ACTION_KINDS as k (k.value)}<option value={k.value}>{k.label}</option>{/each}
								</Select>
								{#if a.kind === "recipe"}
									<Select class="tw-w-48" value={a.target || ""} on:change={(e) => patchAction(r, i, { target: e.target.value })}>
										<option value="">— 고르기 —</option>
										{#each list.filter((x) => x.id !== r.id) as x (x.id)}
											<option value={x.id}>{x.name || x.id}{x.kind === "event" ? " (경품 이벤트)" : ""}</option>
										{/each}
									</Select>
								{:else if a.kind === "tile"}
									<Select class="tw-w-48" value={a.target || ""} on:change={(e) => patchAction(r, i, { target: e.target.value })}>
										<option value="">— 고르기 —</option>
										{#each tiles as t (t.id)}<option value={t.id}>{t.label || t.id}</option>{/each}
									</Select>
								{:else if a.kind === "link"}
									<Input class="tw-w-48" placeholder="https://…" value={a.target || ""} on:input={(e) => patchAction(r, i, { target: e.target.value })} />
								{/if}
								<button type="button" class="del-btn" aria-label="버튼 삭제" on:click={() => removeAction(r, i)}>✕</button>
							</div>
							{#if a.kind === "recipe" && a.target}
								{#if list.find((x) => x.id === a.target)?.kind === "event"}
									<p class="tw-pl-1 tw-text-xs tw-text-muted-foreground">경품 이벤트로 갑니다 — 오늘 참여를 다 했으면 이 버튼이 그 이벤트의 '다 쓴 뒤 버튼' 문구로 꺼집니다.</p>
								{:else}
									<!-- ⭐ 진입점이 '다른 레시피'라는 사실이 생기는 유일한 자리. 이걸 켜야만
									     뒤 레시피가 앞 결과를 본다(메인 타일로 들어오면 안 본다). -->
									<label class="tw-flex tw-items-center tw-gap-1.5 tw-pl-1 tw-text-xs">
										<input type="checkbox" checked={a.carry === true} on:change={(e) => patchAction(r, i, { carry: e.target.checked })} />
										이 결과를 물려주기 <span class="tw-text-muted-foreground">— 다음 레시피가 이 카드·본문을 이어받습니다</span>
									</label>
								{/if}
							{/if}
						{/each}
						<div><Button variant="outline" size="sm" on:click={() => addAction(r)}>+ 버튼 추가</Button></div>
					</div>
					{#if hasEventAction(r)}
						<!-- ⭐ '오늘은 이미 받았어요' 창(런타임 components/DoneModal · 시안 2329:1919 / 2329:1894) 문구.
						     하루 횟수를 다 쓴 사람이 이 레시피를 다시 누르면 결과 대신 이 창이 뜬다 — 이어진 경품 이벤트로 가는 길.
						     칸을 누르면 미리보기에 그 모양이 뜬다. 비우면 중립 기본 문구. -->
						<div class="tw-mt-2 tw-border-t tw-border-border tw-pt-3 tw-text-sm tw-font-semibold">오늘 다 받은 뒤 다시 누르면</div>
						<p class="step-desc">
							하루 횟수를 다 쓴 사람이 이 레시피를 다시 누르면 결과 대신 창이 뜹니다. 이어진 경품 이벤트에 <strong>아직 참여할 수 있으면</strong>
							그 이벤트 버튼이, <strong>이벤트도 오늘 다 했으면</strong> [닫기]만 나옵니다. 칸을 누르면 미리보기에 그 모양이 뜹니다.
						</p>
						<p class="tw-text-xs tw-font-semibold tw-text-muted-foreground">이벤트에 아직 참여할 수 있을 때</p>
						<FieldRow label="제목">
							<Input placeholder="예) 행운빵은 이미 받았어요!" value={r.done?.title || ""} on:focus={() => focusDone(r, false)} on:input={(e) => patchIn(r, "done", { title: e.target.value })} />
						</FieldRow>
						<FieldRow label="설명" top>
							<Textarea rows={2} placeholder={"예) 행운 뽑기는 아직 참여할 수 있어요.\n오늘의 행운도 뽑아보세요!"} value={r.done?.text || ""} on:focus={() => focusDone(r, false)} on:input={(e) => patchIn(r, "done", { text: e.target.value })} />
						</FieldRow>
						<p class="tw-text-xs tw-font-semibold tw-text-muted-foreground">이벤트도 오늘 다 했을 때</p>
						<FieldRow label="제목">
							<Input placeholder="예) 오늘의 행운은 모두 받았어요!" value={r.done?.allTitle || ""} on:focus={() => focusDone(r, true)} on:input={(e) => patchIn(r, "done", { allTitle: e.target.value })} />
						</FieldRow>
						<FieldRow label="설명" top>
							<Textarea rows={2} placeholder={"예) 내일 다시 새로운 행운빵과\n행운 뽑기를 만나보세요!"} value={r.done?.allText || ""} on:focus={() => focusDone(r, true)} on:input={(e) => patchIn(r, "done", { allText: e.target.value })} />
						</FieldRow>
					{/if}
				{/if}

				<div class="tw-flex tw-items-center tw-justify-between tw-border-t tw-border-border tw-pt-3">
					<p class="tw-text-xs tw-text-muted-foreground">
						여는 길: [타일] 탭에서 <strong>“런처가 직접 (레시피)”</strong>로 연결하거나, 다른 레시피의 결과 버튼에서 '다른 레시피 열기'로.
					</p>
					<button type="button" class="tw-shrink-0 tw-text-xs tw-text-destructive tw-underline" on:click={() => remove(r.id)}>이 레시피 삭제</button>
				</div>
			</div>
		{/if}
	</Section>
</div>

<ImagePicker
	bind:open={pickerOpen}
	chatId={bot.id}
	contextFolder={botAssetFolder(bot)}
	mode="single"
	on:select={onPick}
/>
<!-- 카드 한꺼번에 — 여러 장 고르기. 닫기만 하면(add 없음) 아무것도 안 바뀐다. -->
<ImagePicker
	bind:open={bulkOpen}
	chatId={bot.id}
	contextFolder={botAssetFolder(bot)}
	mode="multi"
	existingAssetIds={bulkExisting}
	on:add={onBulkAdd}
/>

<style>
	/* 단계 설명 한 줄 — 단계마다 '이 화면이 무엇인지'를 먼저 말한다. */
	.step-desc {
		margin: 0;
		font-size: 13px;
		line-height: 1.55;
		color: hsl(var(--sb-muted-foreground));
	}
	/* 그림 칸 줄 — 이름표가 아래 붙은 썸네일들을 한 줄로(넘치면 접힌다). */
	/* 이름표 높이 맞춤은 ImageSlot 이 한다(썸네일 자리 높이 고정). */
	.slots {
		display: flex;
		flex-wrap: wrap;
		align-items: flex-start;
		gap: 16px;
		padding: 12px;
		border-radius: 10px;
		background: hsl(var(--sb-muted) / 0.5);
	}
	/* 한 단계 안의 소제목(말풍선과 버튼 · 연출 · 본문 …). */
	.group-title {
		margin-top: 6px;
		padding-top: 12px;
		border-top: 1px solid hsl(var(--sb-border));
		font-size: 13px;
		font-weight: 600;
		color: hsl(var(--sb-foreground));
	}
	.bulk-note {
		white-space: pre-line;
	}
	.bulk-bar {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px;
	}
	.card-row {
		display: flex;
		align-items: flex-start;
		gap: 12px;
		padding: 10px;
		border-radius: 10px;
		border: 1px solid hsl(var(--sb-border));
	}
	.action-row {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px;
	}
	.del-btn {
		flex-shrink: 0;
		width: 28px;
		height: 28px;
		border-radius: 6px;
		border: 1px solid transparent;
		background: transparent;
		color: hsl(var(--sb-destructive));
		font-size: 12px;
		cursor: pointer;
	}
	.del-btn:hover {
		border-color: hsl(var(--sb-destructive) / 0.4);
	}
	.pv-btn {
		padding: 4px 10px;
		border-radius: 6px;
		border: 1px solid hsl(var(--sb-border));
		background: hsl(var(--sb-background));
		font-size: 12px;
		cursor: pointer;
	}
	.pv-btn:hover {
		background: hsl(var(--sb-muted));
	}
	.color-input {
		width: 44px;
		height: 36px;
		flex-shrink: 0;
		padding: 2px;
		border-radius: 6px;
		border: 1px solid hsl(var(--sb-border));
		background: transparent;
		cursor: pointer;
	}
	.swatch {
		width: 24px;
		height: 24px;
		flex-shrink: 0;
		border-radius: 6px;
		border: 1px solid hsl(var(--sb-border));
	}
	code {
		font-size: 11px;
		padding: 0 3px;
		border-radius: 3px;
		background: hsl(var(--sb-muted));
	}
</style>
